<script setup lang="ts">
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";

import {useField} from "vee-validate";
import {useAuthStore} from "@stores/auth";
import ApiService from "@services/ApiService";

const authStore = useAuthStore();

const emit = defineEmits(["completed"]);

const documentModel = ref("");
const isLoading = ref(false);
const isDocumentApplied = ref(false);
const errors = ref("");

const {errorMessage, meta, value} = useField("user-document");

function closeModal() {
  authStore.toggleUserDocumentModal();
  documentModel.value = "";
  isDocumentApplied.value = false;
  isLoading.value = false;
}

function cleanErrors() {
  if (errors.value) {
    errors.value = "";
  }
}

async function submit() {

  isLoading.value = true;

  const {data} = await ApiService.post("/api/validate/document", {
    document: documentModel.value,
  });

  if (data.data?.errors) {
    errors.value = "Digite um CPF válido";
    isLoading.value = false;
    return;
  }

  const response = await ApiService.post("/api/auth/document", {
    document: documentModel.value,
  });

  if (response.data.success) {
    await authStore.getUser();
    isDocumentApplied.value = true;

    setTimeout(() => {
      authStore.toggleUserDocumentModal();
      emit("completed");
    }, 1500);
  }

  if (response.data.errors) {
    errors.value = response.data.message;
  }

  isLoading.value = false;
}
</script>

<template>
  <TransitionRoot
      appear
      :show="authStore.isUserDocumentModalOpen"
      as="template"
  >
    <Dialog as="div" @close="closeModal" class="relative z-[1000]">
      <TransitionChild
          as="template"
          enter="duration-300 ease-out"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="duration-200 ease-in"
          leave-from="opacity-100"
          leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/80"/>
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div
            class="flex min-h-full items-center justify-center p-4 text-center"
        >
          <TransitionChild
              as="template"
              enter="duration-300 ease-out"
              enter-from="opacity-0 scale-95"
              enter-to="opacity-100 scale-100"
              leave="duration-200 ease-in"
              leave-from="opacity-100 scale-100"
              leave-to="opacity-0 scale-95"
          >
            <DialogPanel
                class="relative w-full max-w-md transform overflow-hidden rounded-md bg-secondary p-6 text-left align-middle shadow-xl transition-all"
            >
              <DialogTitle as="div" class="mb-8 flex flex-col justify-between">
                <h3 class="text-lg font-medium leading-6 text-white">
                  Adicione seu documento
                </h3>
                <p class="text-xs text-gray-400">
                  É necessário adicionar seu CPF para depositar
                </p>
              </DialogTitle>

              <button
                  @click="closeModal"
                  class="absolute right-3 top-3 flex items-center justify-center text-lg text-brand-primary/50 transition-all hover:text-brand-primary"
              >
                <Icon name="fa6-solid:circle-xmark"/>
              </button>

              <div class="mt-2">
                <div class="mt-3 flex items-stretch gap-2">
                  <BaseInput
                      type="text"
                      name="user-document"
                      class="flex-1 text-white"
                      placeholder="CPF"
                      :error="errors"
                      v-model="documentModel"
                      uppercase="true"
                      rules="cpf"
                      mask="###.###.###-##"
                      @keydown.enter="submit()"
                      @click="cleanErrors()"
                  />
                  {{ errorMessage }}
                  <BaseButton
                      variant="secondary"
                      class="min-h-[52px] min-w-[90px] self-start"
                      :loading="isLoading && !isDocumentApplied"
                      :completed="isDocumentApplied"
                      :disabled="isLoading"
                      @click="submit()"
                  >
                    Adicionar
                  </BaseButton>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<style scoped></style>
